import { ScenarioProject } from "in_queue/types/scenarioResultType";
import { MisoPercentageReduction } from "in_queue/types/scenarioType";
import { isProjectExcluded } from "in_queue/types/scenarioUtils";
import { rankProjectsByCostPerMw } from "in_queue/utils/rankFormatting";

export type RankingCostMetric = "perMw" | "total";
export type RankingCohort = "RELATED" | "ALL";

export interface RankingDot {
    projectId: string;
    value: number;
    isYou: boolean;
    isPortfolio: boolean;
    rank: number;
    total: number;
    percentile: number;
    sumAllocatedCost: number;
    costPerMw: number | undefined;
}

interface BuildRankingDotsArgs {
    projects: ReadonlyArray<ScenarioProject>;
    assumptions: Record<string, MisoPercentageReduction>;
    metric: RankingCostMetric;
    cohort: RankingCohort;
    relatedIds: ReadonlySet<string>;
    currentProjectId: string;
    portfolioIds: ReadonlySet<string>;
    /** Narrows the cohort further, e.g. to the current project's fuel type. */
    include?: (sp: ScenarioProject) => boolean;
}

const costValue = (
    sp: ScenarioProject,
    metric: RankingCostMetric
): number | undefined =>
    metric === "perMw" ? sp.costPerMw : sp.sumAllocatedCost;

/**
 * One dot per ranked project, sorted ascending by the chosen cost.
 * Projects the scenario sized out (eris = nris = 100) still land in
 * scenario_projects at $0. The pipeline's rank pool drops them, so
 * this does too.
 */
export const buildRankingDots = ({
    projects,
    assumptions,
    metric,
    cohort,
    relatedIds,
    currentProjectId,
    portfolioIds,
    include = () => true
}: BuildRankingDotsArgs): RankingDot[] => {
    const pool = projects.filter((sp) => {
        if (isProjectExcluded(assumptions[sp.projectId])) return false;
        if (!include(sp)) return false;
        if (!Number.isFinite(costValue(sp, metric))) return false;
        if (sp.projectId === currentProjectId) return true;
        return cohort === "ALL" || relatedIds.has(sp.projectId);
    });
    const getValue = (sp: ScenarioProject): number =>
        costValue(sp, metric) ?? 0;
    const sorted = pool.slice().sort((a, b) => getValue(a) - getValue(b));
    const { rankById, percentileById, total } = rankProjectsByCostPerMw(
        sorted,
        (sp) => sp.projectId,
        getValue
    );
    return sorted.map((sp) => ({
        projectId: sp.projectId,
        value: getValue(sp),
        isYou: sp.projectId === currentProjectId,
        isPortfolio: portfolioIds.has(sp.projectId),
        rank: rankById[sp.projectId],
        total,
        percentile: percentileById[sp.projectId],
        sumAllocatedCost: sp.sumAllocatedCost,
        costPerMw: sp.costPerMw
    }));
};
